// SectorsPanel — Story #6837 Stream 8a-2 (figure 1 = screen-M1-sectors.png).
//
// Detail rows (role, label · ISIC code · ANZSIC division, Auto / Owner chip,
// confidence chip, CPC cross-check), ↑ ↓ ✕, Removed + Restore, an inline
// "Add a sector" search over the taxonomy with an ISIC / ANZSIC toggle, and
// "Re-run auto-categorisation". Every write goes through putMarketSectors and
// then invalidates ['market', projectId]. insufficient_basis shows the
// ConfidenceGate (ADR-022: nothing is fabricated).
//
// Right rail: the figure's "Sector snapshot" and "How the assignment was made".
// Figure 1's "Filing momentum" tile, the per-run document counts, the "Open
// Outlook" link and the "View run" link have no data source in the Market GET
// (and MarketPanelProps carries no tab switch), so they are not drawn rather
// than invented.
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, ArrowDown, ArrowUp, Info, Plus, RefreshCw, Search, X } from 'lucide-react'
import { getMarketTaxonomy, putMarketSectors } from '../../services/api'
import type {
  MarketPanelProps, MarketReferenceRow, MarketTaxonomy,
} from '../../types/market'
import { PESTLE_CELLS } from '../../types/market'
import ConfidenceGate from '../shared/ConfidenceGate'
import { dateLabel, money } from './marketFormat'
import { runMarketChain } from './useMarketAutoRun'
import {
  MAX_SECTORS, planAdd, planDemote, planPromote, planRemove, planRestore,
  type SectorWrite,
} from './sectorEdits'
import { invalidateMarket } from './marketQueries'

const MAX_RESULTS = 8

interface TaxonomyClass {
  code: string
  name: string
  division: string
  section: string
  anzsic: string[]
}

type SearchMode = 'isic' | 'anzsic'

const CARD = 'bg-white/70 border border-[rgba(12,74,110,0.12)] rounded-xl p-4'

function flattenTaxonomy(tax: MarketTaxonomy | undefined): TaxonomyClass[] {
  if (!tax) return []
  const out: TaxonomyClass[] = []
  for (const s of tax.sections) {
    const anzsic = tax.isic_section_to_anzsic_division[s.code] ?? []
    for (const d of s.divisions) {
      for (const c of d.classes) out.push({ code: c.code, name: c.name, division: d.code, section: s.code, anzsic })
    }
  }
  return out
}

function confidenceBand(c: number): 'high' | 'medium' | 'low' {
  return c >= 0.75 ? 'high' : c >= 0.5 ? 'medium' : 'low'
}

const BAND_STYLE = {
  high: 'bg-emerald-50 text-emerald-700',
  medium: 'bg-amber-50 text-amber-700',
  low: 'bg-red-50 text-red-700',
} as const

type CpcVerdict = 'agrees' | 'disagrees' | 'no_signal'

/** cpc_check is a plain string on the live API; an object form is tolerated (unpinned). */
function cpcVerdict(v: unknown): CpcVerdict | null {
  const raw = typeof v === 'string'
    ? v
    : v && typeof v === 'object'
      ? String((v as Record<string, unknown>).result ?? (v as Record<string, unknown>).verdict ?? (v as Record<string, unknown>).status ?? '')
      : ''
  return raw === 'agrees' || raw === 'disagrees' || raw === 'no_signal' ? raw : null
}

const CPC_STYLE: Record<CpcVerdict, string> = {
  agrees: 'text-emerald-700',
  disagrees: 'text-red-700',
  no_signal: 'text-amber-700',
}

const SOURCE_NAME: Record<string, string> = { abs: 'ABS', worldbank: 'World Bank', oecd: 'OECD' }

function httpMessage(err: unknown): string {
  const e = err as { response?: { data?: { error?: unknown } }; message?: string } | null | undefined
  const body = e?.response?.data?.error
  return typeof body === 'string' && body ? body : e?.message || 'The change could not be saved.'
}

export default function SectorsPanel({ projectId, market, country, canRun }: MarketPanelProps) {
  const qc = useQueryClient()
  const [addOpen, setAddOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [mode, setMode] = useState<SearchMode>('isic')
  const [sessionRemoved, setSessionRemoved] = useState<string[]>([])
  const [rerunning, setRerunning] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  const { data: taxonomy } = useQuery({
    queryKey: ['market-taxonomy'],
    queryFn: getMarketTaxonomy,
    staleTime: Infinity,
  })
  const classes = useMemo(() => flattenTaxonomy(taxonomy), [taxonomy])
  const byCode = useMemo(() => new Map(classes.map((c) => [c.code, c])), [classes])

  const detail = market.detail
  const assigned = new Set(detail.map((d) => d.code))
  const knownRemoved = [...new Set([...(market.removed ?? []), ...sessionRemoved])].filter((c) => !assigned.has(c))
  const atLimit = detail.length >= MAX_SECTORS

  const save = useMutation({
    mutationFn: (v: SectorWrite) =>
      putMarketSectors(projectId, v.removed === undefined ? { sectors: v.sectors } : { sectors: v.sectors, removed: v.removed }),
    onSuccess: () => {
      setMessage(null)
      return invalidateMarket(qc, projectId)   // stay pending until fresh data: a fast second edit must not be planned from the old list
    },
    onError: (err) => setMessage(httpMessage(err)),
  })
  const busy = save.isPending || rerunning

  // The removed list is sent only when this panel actually knows it: an API that does
  // not return `removed` yet, with nothing removed this session, is left untouched.
  const removedKnown = market.removed !== undefined || sessionRemoved.length > 0
  const keepRemoved = removedKnown ? knownRemoved : undefined
  const run = (plan: SectorWrite | null) => { if (plan) save.mutate(plan) }

  const makePrimary = (code: string) => run(planPromote(detail, keepRemoved, code))
  const demotePrimary = () => run(planDemote(detail, keepRemoved))
  const remove = (code: string) => {
    const plan = planRemove(detail, keepRemoved, code)
    if (!plan) return
    setSessionRemoved((r) => [...new Set([...r, code])])
    save.mutate(plan)
  }
  const add = (code: string) => run(planAdd(detail, keepRemoved, code))
  const restore = (code: string) => {
    const plan = planRestore(detail, keepRemoved, code)
    if (!plan) {
      setMessage(`A maximum of ${MAX_SECTORS} sectors can be assigned — remove one before restoring another.`)
      return
    }
    setSessionRemoved((r) => r.filter((c) => c !== code))
    save.mutate(plan)
  }

  const rerun = async () => {
    setRerunning(true)
    setMessage(null)
    const res = await runMarketChain(projectId, { country, force: true, classifyFirst: true })
    setRerunning(false)
    if (res.phase === 'error') setMessage(res.error)
    else if (res.phase === 'rate_limited') setMessage(`Too many market analyses right now${res.retryAfter != null ? ` — try again in ${res.retryAfter} s` : ''}.`)
    void invalidateMarket(qc, projectId)
  }

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q || !taxonomy) return []
    return classes.filter((c) => {
      if (mode === 'isic') return c.code.startsWith(q) || c.name.toLowerCase().includes(q)
      return c.anzsic.some((l) => l.toLowerCase() === q || (taxonomy.anzsic_divisions[l] ?? '').toLowerCase().includes(q))
    })
  }, [classes, query, mode, taxonomy])

  // ── Right rail ────────────────────────────────────────────────────────────
  const refRows: MarketReferenceRow[] = market.reference
  const auSize = refRows.find((r) => r.country === 'AUS' && r.unit === 'AUD million')
    ?? refRows.find((r) => r.country === 'AUS' && r.unit === 'USD')
  const worldAnchor = refRows.find((r) => r.country === 'WLD' && r.unit === 'USD')
  const pestleOutlook = (() => {
    if (!market.pestle) return null
    let score = 0
    for (const cell of PESTLE_CELLS) {
      for (const item of market.pestle.cells[cell] ?? []) {
        score += item.signal === 'supportive' ? 1 : item.signal === 'tightening' ? -1 : 0
      }
    }
    return { label: score > 0 ? 'Favourable' : score < 0 ? 'Tightening' : 'Mixed', at: dateLabel(market.pestle.generated_at) }
  })()
  const lastRun = dateLabel(detail.reduce<number | undefined>((m, d) => (typeof d.set_at === 'number' && (m === undefined || d.set_at > m) ? d.set_at : m), undefined))

  const tile = (label: string, value: string | null, sub: string | null) => (
    <div className="rounded-lg border border-[rgba(12,74,110,0.12)] bg-white/70 p-2.5">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{label}</div>
      <div className="text-lg font-bold text-iflow-900">{value ?? 'Not available'}</div>
      {sub && <div className="text-[10.5px] italic text-gray-500">{sub}</div>}
    </div>
  )

  const showGate = market.status === 'insufficient_basis'
  const summaryOnly = detail.length === 0 && market.sectors.length > 0

  return (
    <div data-testid="sectors-panel" className="space-y-3">
      <div className="flex gap-2 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2.5 text-xs text-iflow-900">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sky-600" />
        <p>
          <strong>Sectors are assigned when the invention is first analysed.</strong> The first analysis reads your
          documents and proposes a primary sector and up to two secondary ones, coded to ISIC Rev.4 (with the ANZSIC 2006
          equivalent). Each proposal is cross-checked against the CPC codes of the closest prior art. You can add, change
          or remove any of them — your edits are kept when the analysis re-runs.
        </p>
      </div>

      <div className="grid gap-3 lg:grid-cols-3">
        <div className={`${CARD} lg:col-span-2`}>
          <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
            <div>
              <h3 className="text-sm font-bold text-iflow-900">Market sectors</h3>
              <p className="text-[11px] text-gray-500">
                {detail.length || market.sectors.length} assigned
                {knownRemoved.length > 0 ? ` · ${knownRemoved.length} removed` : ''} · taxonomy ISIC Rev.4 / ANZSIC 2006
              </p>
            </div>
            {canRun && (
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="sectors-rerun"
                  disabled={busy}
                  onClick={rerun}
                  className="flex items-center gap-1 rounded-lg border border-[rgba(12,74,110,0.2)] bg-white px-2.5 py-1.5 text-xs font-medium text-iflow-700 disabled:opacity-50"
                >
                  <RefreshCw className={`h-3 w-3 ${rerunning ? 'animate-spin' : ''}`} /> Re-run auto-categorisation
                </button>
                <button
                  type="button"
                  data-testid="sectors-add-open"
                  disabled={busy}
                  onClick={() => setAddOpen((o) => !o)}
                  className="flex items-center gap-1 rounded-lg bg-sky-500 px-2.5 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                >
                  <Plus className="h-3 w-3" /> Add sector
                </button>
              </div>
            )}
          </div>

          {message && (
            <div data-testid="sectors-message" role="alert" className="mb-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
              {message}
            </div>
          )}

          {showGate && (
            <ConfidenceGate
              compact
              icon={<AlertTriangle />}
              title="Not enough information to assign a sector"
              description="The documents on this invention do not support a confident classification, so no sector was assigned — nothing is guessed. Add documents and re-run, or add a sector yourself."
              action={canRun ? { label: 'Re-run auto-categorisation', onClick: rerun } : undefined}
            />
          )}

          {!showGate && detail.length === 0 && !summaryOnly && (
            <p data-testid="sectors-empty" className="py-6 text-center text-xs text-gray-500">
              {canRun
                ? 'Not categorised yet — the first analysis runs when this tab opens.'
                : 'Not categorised yet. The owner starts the first analysis from this tab.'}
            </p>
          )}

          {summaryOnly && (
            <ul data-testid="sectors-summary" className="divide-y divide-[rgba(12,74,110,0.1)]">
              {market.sectors.map((s) => (
                <li key={`${s.section}${s.division}`} className="flex items-center gap-3 py-2 text-xs">
                  <span className="w-20 text-[10px] font-semibold uppercase text-gray-500">{s.role}</span>
                  <span className="font-semibold text-iflow-900">Section {s.section} · division {s.division}</span>
                </li>
              ))}
              <li className="pt-2 text-[11px] text-gray-500">Sector detail is visible only to the organisation that owns this invention.</li>
            </ul>
          )}

          {detail.length > 0 && (
            <ul className="divide-y divide-[rgba(12,74,110,0.1)]" data-testid="sectors-list">
              {detail.map((d) => {
                const isPrimary = d.role === 'primary'
                const cpc = cpcVerdict(d.cpc_check)
                const band = confidenceBand(d.confidence)
                return (
                  <li key={d.code} data-testid={`sector-row-${d.code}`} className="flex gap-3 py-2.5">
                    <div className="w-20 shrink-0 pt-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      {isPrimary ? 'Primary' : 'Secondary'}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-bold text-iflow-900">
                        {d.label}
                        <span className="ml-1 text-[11px] font-normal text-gray-500">
                          · ISIC {d.code} · ANZSIC {d.anzsic_divisions.join(', ') || '—'}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
                        {d.source === 'owner' ? (
                          <>
                            <span data-testid="chip-source" className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-violet-800">Owner</span>
                            <span className="text-gray-500">kept on re-run</span>
                          </>
                        ) : (
                          <>
                            <span data-testid="chip-source" className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-sky-800">Auto</span>
                            <span data-testid="chip-confidence" className={`rounded-full px-2 py-0.5 font-medium ${BAND_STYLE[band]}`}>
                              Confidence {band} · {d.confidence.toFixed(2)}
                            </span>
                          </>
                        )}
                        {cpc && (
                          <span data-testid="chip-cpc" className="text-gray-600">
                            CPC cross-check: <strong className={CPC_STYLE[cpc]}>{cpc === 'no_signal' ? 'no signal' : cpc}</strong>
                          </span>
                        )}
                      </div>
                    </div>
                    {canRun && (
                      <div className="flex shrink-0 items-start gap-1.5">
                        {isPrimary ? (
                          detail.length > 1 && (
                            <button type="button" aria-label={`Demote ${d.label}`} data-testid={`sector-down-${d.code}`} disabled={busy} onClick={demotePrimary}
                              className="rounded-md border border-[rgba(12,74,110,0.2)] bg-white p-1.5 text-gray-600 disabled:opacity-50">
                              <ArrowDown className="h-3.5 w-3.5" />
                            </button>
                          )
                        ) : (
                          <button type="button" aria-label={`Make ${d.label} primary`} data-testid={`sector-up-${d.code}`} disabled={busy} onClick={() => makePrimary(d.code)}
                            className="rounded-md border border-[rgba(12,74,110,0.2)] bg-white p-1.5 text-gray-600 disabled:opacity-50">
                            <ArrowUp className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button type="button" aria-label={`Remove ${d.label}`} data-testid={`sector-remove-${d.code}`} disabled={busy || detail.length < 2} onClick={() => remove(d.code)}
                          title={detail.length < 2 ? 'At least one sector is required' : undefined}
                          className="rounded-md border border-[rgba(12,74,110,0.2)] bg-white p-1.5 text-gray-600 disabled:opacity-40">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </li>
                )
              })}
              {knownRemoved.map((code) => {
                const c = byCode.get(code)
                return (
                  <li key={code} data-testid={`sector-removed-${code}`} className="flex gap-3 py-2.5">
                    <div className="w-20 shrink-0 pt-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Removed</div>
                    <div className="min-w-0 flex-1 text-sm text-gray-500">
                      <span className="font-semibold line-through">{c?.name ?? `ISIC ${code}`}</span>
                      <span className="ml-1 text-[11px] line-through">· ISIC {code}</span>
                      <div className="text-[11px]">
                        Removed by you — auto-categorisation will not add it back.{' '}
                        {canRun && (
                          <button type="button" data-testid={`sector-restore-${code}`} disabled={busy} onClick={() => restore(code)} className="font-medium text-sky-600 underline disabled:opacity-50">
                            Restore
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}

          <p className="mt-3 text-[11px] text-gray-500">
            Why coded sectors: ISIC / ANZSIC codes are what the official statistics are published against, so each sector
            here joins directly to the sector size on the Outlook tab.
          </p>

          {canRun && addOpen && (
            <div data-testid="sectors-add" className="mt-3 rounded-lg border border-sky-200 bg-sky-50/60 p-3">
              <div className="mb-2 text-xs font-bold text-iflow-900">Add a sector</div>
              <div className="flex items-center gap-2 rounded-lg border border-sky-400 bg-white px-2 py-1.5">
                <Search className="h-3.5 w-3.5 text-gray-400" />
                <input
                  aria-label="Search sectors"
                  data-testid="sectors-search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={mode === 'isic' ? 'Search ISIC classes by name or code' : 'Search ANZSIC divisions by letter or name'}
                  className="min-w-0 flex-1 bg-transparent text-xs outline-none"
                />
                <div className="flex gap-0.5 rounded-md bg-sky-500/10 p-0.5" role="tablist" aria-label="Taxonomy">
                  {(['isic', 'anzsic'] as const).map((m) => (
                    <button key={m} type="button" role="tab" aria-selected={mode === m} data-testid={`sectors-mode-${m}`} onClick={() => setMode(m)}
                      className={`rounded px-2 py-0.5 text-[10.5px] font-semibold ${mode === m ? 'bg-white text-iflow-700 shadow-sm' : 'text-gray-500'}`}>
                      {m === 'isic' ? 'ISIC' : 'ANZSIC'}
                    </button>
                  ))}
                </div>
              </div>
              <ul className="mt-1 divide-y divide-[rgba(12,74,110,0.1)]">
                {matches.slice(0, MAX_RESULTS).map((c) => (
                  <li key={c.code} data-testid={`sector-result-${c.code}`} className="flex items-center justify-between gap-2 py-2">
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-iflow-900">{c.code} {c.name}</div>
                      <div className="text-[11px] text-gray-500">
                        {mode === 'isic'
                          ? `ISIC Rev.4 · Division ${c.section}${c.division}`
                          : `ANZSIC ${c.anzsic.map((l) => `${l} ${taxonomy?.anzsic_divisions[l] ?? ''}`).join(', ')} · ISIC ${c.section}${c.division}`}
                        {assigned.has(c.code) ? ' · already assigned' : ''}
                      </div>
                    </div>
                    {assigned.has(c.code) ? (
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">Assigned</span>
                    ) : (
                      <button type="button" data-testid={`sector-add-${c.code}`} disabled={busy || atLimit} onClick={() => add(c.code)}
                        className="rounded-lg bg-sky-500 px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-40">
                        Add
                      </button>
                    )}
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-[11px] text-gray-500">
                {atLimit
                  ? `A maximum of ${MAX_SECTORS} sectors can be assigned — remove one to add another.`
                  : `Search by name or code · ${Math.min(matches.length, MAX_RESULTS)} of ${classes.length} classes shown`}
              </p>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div className={CARD} data-testid="sector-snapshot">
            <h3 className="mb-2 text-sm font-bold text-iflow-900">
              Sector snapshot <span className="text-[11px] font-normal text-gray-500">· primary sector · Australia</span>
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {tile(
                'Sector size (AU)',
                auSize ? money(auSize.unit === 'AUD million' ? auSize.value * 1e6 : auSize.value, auSize.unit === 'AUD million' ? 'A$' : 'US$') : null,
                auSize ? `${SOURCE_NAME[auSize.source] ?? auSize.source} · ${auSize.year}` : null,
              )}
              {tile(
                'Global anchor',
                worldAnchor ? money(worldAnchor.value, 'US$') : null,
                worldAnchor ? `${SOURCE_NAME[worldAnchor.source] ?? worldAnchor.source} · ${worldAnchor.year}` : null,
              )}
              {tile('PESTLE outlook', pestleOutlook?.label ?? null, pestleOutlook?.at ? `as at ${pestleOutlook.at}` : null)}
            </div>
            <p className="mt-2 text-[11px] text-gray-500">Official sector statistics are shown at the finest level the source publishes.</p>
          </div>

          <div className={CARD} data-testid="sector-method">
            <h3 className="mb-2 text-sm font-bold text-iflow-900">How the assignment was made</h3>
            <ul className="list-disc space-y-1 pl-4 text-[11.5px] text-gray-700">
              <li>Model proposal → taxonomy code, with a confidence per sector.</li>
              <li>Cross-check against the CPC codes of the nearest patents.</li>
              <li>Owner edits are stored separately and never overwritten.</li>
            </ul>
            {lastRun && <p className="mt-2 text-[11px] text-gray-500">Last assigned {lastRun}.</p>}
          </div>
        </div>
      </div>
    </div>
  )
}
